// @ts-nocheck
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  Button,
  Divider,
  Grid,
  Icon,
  TextField,
  Typography,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';

const useStyles = makeStyles(theme => ({
  cardNumber: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  cardId: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  cardPlaceholder: {
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1),
  },
  caption: {
    marginTop: theme.spacing(1),
  },
  info: {
    marginTop: theme.spacing(2),
  },
  step: {
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  bullet: {
    height: '24px',
    width: '24px',
    borderRadius: '50%',
    marginRight: theme.spacing(2),
  },
  divider: {
    flex: '1',
  },
  searchButton: {
    marginLeft: theme.spacing(2),
  },
  partnerSearch: {
    boxShadow: 'none',
    marginTop: theme.spacing(1),
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      marginTop: theme.spacing(1),
    },
    '& .MuiAccordionSummary-root': {
      padding: '0',
    },
    '& .MuiAccordionSummary-root.Mui-expanded': {
      minHeight: '48px',
    },
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      color: theme.palette.primary.main,
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: '0',
    },
    '& .MuiAccordionDetails-root': {
      padding: theme.spacing(1, 0, 0, 0),
    },
  },
  partnerSearchLabel: {
    padding: theme.spacing(0, 2, 0, 1),
  },
}));

function CustomerSearchForm(props) {
  const [t] = useTranslation();
  const { formState, control, register, handleSubmit } = useForm();
  const classes = useStyles();

  const { partners, onSearch } = props;
  const { isSubmitting, isDirty, dirtyFields } = formState;

  const onSubmit = async data => {
    const formData = omitEmpty(data);
    if (data.partnerid && !data.vastoken) {
      _.unset(formData, 'partnerid');
    }
    await onSearch(formData);
  };

  const isFieldDisabled = (deps = []) => {
    const fields = _.omit(dirtyFields, [...deps, 'partnerid']);
    return !_.isEmpty(fields);
  };

  return (
    <Box id="CustomerSearchForm-container">
      <form id="CustomerSearchForm-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid item xs={3}>
            <Box className={classes.step}>
              <Icon color="primary" className={classes.bullet}>
                account_circle
              </Icon>
              <Divider className={classes.divider} />
            </Box>
            <TextField
              id="CustomerSearchForm-term"
              name="searchterm"
              label={t('customer.customerSearchTerm')}
              variant="outlined"
              size="small"
              inputRef={register}
              disabled={isFieldDisabled(['searchterm'])}
              fullWidth
            />
            <Typography variant="caption" component="div" className={classes.caption}>
              {t('customer.customerSearchTermCaption')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Box className={classes.step}>
              <Icon color="primary" className={classes.bullet}>
                assignment
              </Icon>
              <Divider className={classes.divider} />
            </Box>
            <TextField
              id="CustomerSearchForm-orderId"
              name="orderid"
              label={t('customer.customerSearchOrder')}
              variant="outlined"
              size="small"
              inputRef={register}
              disabled={isFieldDisabled(['orderid'])}
              fullWidth
            />
            <Typography variant="caption" component="div" className={classes.caption}>
              {t('customer.customerSearchOrderCaption')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Box className={classes.step}>
              <Icon color="primary" className={classes.bullet}>
                payment
              </Icon>
              <Divider className={classes.divider} />
            </Box>
            <Box className={classes.cardNumber}>
              <Box className={classes.cardPlaceholder}>
                <Typography>XXXX-XXXX-XXXX-</Typography>
              </Box>
              <TextField
                id="CustomerSearchForm-cardNumber"
                name="cardno"
                label={t('customer.customerSearchCardNumber')}
                variant="outlined"
                size="small"
                inputProps={{ maxLength: 4 }}
                inputRef={register}
                disabled={isFieldDisabled(['cardno', 'expmonth', 'expyear'])}
                fullWidth
              />
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  id="CustomerSearchForm-cardMonth"
                  name="expmonth"
                  label={t('customer.customerSearchCardMonth')}
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 2 }}
                  inputRef={register}
                  disabled={isFieldDisabled(['cardno', 'expmonth', 'expyear'])}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="CustomerSearchForm-cardYear"
                  name="expyear"
                  label={t('customer.customerSearchCardYear')}
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 4 }}
                  inputRef={register}
                  disabled={isFieldDisabled(['cardno', 'expmonth', 'expyear'])}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box className={classes.cardId}>
              <TextField
                id="CustomerSearchForm-cardId"
                name="cardId"
                label={t('customer.customerSearchCardId')}
                variant="outlined"
                size="small"
                inputRef={register}
                fullWidth
              />
            </Box>
            <Typography variant="caption" component="div" className={classes.caption}>
              {t('customer.customerSearchCardCaption')}
              <br />
              {t('customer.customerSearchCardCaptionRequired')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Box className={classes.step}>
              <Icon color="primary" className={classes.bullet}>
                info
              </Icon>
              <Divider className={classes.divider} />
              <Button
                id="CustomerSearchForm-submit"
                type="submit"
                size="large"
                color="primary"
                className={classes.searchButton}
                startIcon={<Icon>search</Icon>}
                disabled={isSubmitting || !isDirty}
              >
                {t('common.search')}
              </Button>
            </Box>
            <Typography variant="body2" className={classes.info}>
              {t('customer.customerSearchInfo_1')}
            </Typography>
            <Typography variant="body2" className={classes.info}>
              {t('customer.customerSearchInfo_2')}
            </Typography>
            <Typography variant="body2" className={classes.info}>
              {t('customer.customerSearchInfo_3')}
            </Typography>
          </Grid>
        </Grid>
        <Accordion id="CustomerSearchForm-partnerSearch" className={classes.partnerSearch}>
          <AccordionSummary
            id="CustomerSearchForm-partnerSearchExpand"
            aria-label="expand"
            expandIcon={<Icon>expand_more</Icon>}
          >
            <Icon>vpn_key</Icon>
            <Typography variant="button" className={classes.partnerSearchLabel}>
              {t('customer.customerSearchPartnerSearch')}
            </Typography>
            <Divider className={classes.divider} />
          </AccordionSummary>
          <AccordionDetails id="CustomerSearchForm-partnerSearchContent">
            <Grid container spacing={4}>
              <Grid item xs={4}>
                <Controller
                  name="partnerid"
                  control={control}
                  defaultValue=""
                  as={
                    <TextField
                      id="CustomerSearchForm-partner"
                      label={t('customer.customerSearchPartner')}
                      variant="outlined"
                      size="small"
                      disabled={_.isEmpty(partners)}
                      fullWidth
                      select
                    >
                      {partners.map(partner => (
                        <MenuItem key={partner.id} value={_.toString(partner.id)}>
                          {partner.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />
                <Typography variant="caption" component="div" className={classes.caption}>
                  {t('customer.customerSearchProviderCaption')}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  id="CustomerSearchForm-vasToken"
                  name="vastoken"
                  label={t('customer.customerSearchVasToken')}
                  variant="outlined"
                  size="small"
                  inputRef={register}
                  disabled={!dirtyFields.partnerid}
                  fullWidth
                />
                <Typography variant="caption" component="div" className={classes.caption}>
                  {t('customer.customerSearchVasTokenCaption')}
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </form>
    </Box>
  );
}

CustomerSearchForm.propTypes = {
  partners: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSearch: PropTypes.func.isRequired,
};

export default CustomerSearchForm;
